import type { TypographyOptions } from '@mui/material/styles/createTypography';
import React from 'react';

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    captionText: true;
    caption2Text: true;
    customHeading: true;
    buttonText: true;
    paragraphHeading: true;
    caption3Text: true;
    boldText: true;
    label: true;
    h7: true;
  }
}

interface ExtendedTypographyOptions extends TypographyOptions {
  captionText: React.CSSProperties;
  caption2Text: React.CSSProperties;
  customHeading: React.CSSProperties;
  buttonText: React.CSSProperties;
  paragraphHeading: React.CSSProperties;
  caption3Text: React.CSSProperties;
  boldText: React.CSSProperties;
  label: React.CSSProperties;
  h7: React.CSSProperties;
}

export const createTypography = (): ExtendedTypographyOptions => {
  return {
    fontFamily:
      '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji"',
    body1: {
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.875rem',
      fontWeight: 400,
      lineHeight: 1.57,
    },
    button: {
      fontWeight: 600,
    },
    caption: {
      fontSize: '0.75rem',
      fontWeight: 500,
      lineHeight: 1.66,
    },
    subtitle1: {
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: 1.57,
    },
    subtitle2: {
      fontSize: '0.875rem',
      fontWeight: 500,
      lineHeight: 1.57,
    },
    overline: {
      fontSize: '0.75rem',
      fontWeight: 600,
      letterSpacing: '0.5px',
      lineHeight: 2.5,
      textTransform: 'uppercase',
    },
    h1: {
      fontFamily: "'Plus Jakarta Sans', sans-serif",
      fontWeight: 700,
      fontSize: '3.5rem',
      lineHeight: 1.2,
    },
    h2: {
      fontFamily: "'Plus Jakarta Sans', sans-serif",
      fontWeight: 700,
      fontSize: '3rem',
      lineHeight: 1.2,
    },
    h3: {
      fontFamily: "'Plus Jakarta Sans', sans-serif",
      fontWeight: 700,
      fontSize: '2.25rem',
      lineHeight: 1.2,
    },
    h4: {
      fontFamily: "'Plus Jakarta Sans', sans-serif",
      fontWeight: 700,
      fontSize: '2rem',
      lineHeight: 1.2,
    },
    h5: {
      fontFamily: "'Plus Jakarta Sans', sans-serif",
      fontWeight: 700,
      fontSize: '1.5rem',
      lineHeight: 1.2,
    },
    h6: {
      fontFamily: "'Plus Jakarta Sans', sans-serif",
      fontWeight: 700,
      fontSize: '1.125rem',
      lineHeight: 1.2,
    },
    customHeading: {
      fontFamily: "'Plus Jakarta Sans', sans-serif",
      fontWeight: 700,
      fontSize: '1.125rem',
      lineHeight: 1.2,
    },
    captionText: {
      fontWeight: 500,
      fontSize: '12px',
      fontStyle: 'normal',
      color: '#6C737F',
      lineHeight: '19.92px',
    },
    caption2Text: {
      fontWeight: 400,
      fontSize: '14px',
      fontStyle: 'normal',
      color: '#6C737F',
      lineHeight: '19.92px',
    },
    caption3Text: {
      fontWeight: 400,
      fontSize: '14px',
      fontStyle: 'normal',
      color: '#111927',
      lineHeight: '19.92px',
    },
    buttonText: {
      fontSize: '13px',
      fontWeight: 600,
      letterSpacing: '0.2px',
      lineHeight: 2.2,
    },
    paragraphHeading: {
      fontSize: '12px',
      fontWeight: 600,
      letterSpacing: '0.2px',
      lineHeight: 2.2,
      textTransform: 'uppercase',
    },
    boldText: {
      fontSize: '13px',
      fontWeight: 500,
      letterSpacing: '0.2px',
      lineHeight: 2.2,
      color: '#111927',
    },
    label: {
      fontSize: '10px',
      fontWeight: 500,
      letterSpacing: '0.2px',
      lineHeight: 1.4,
      color: '#9E9E9E',
    },
    h7: {
      fontWeight: 600,
      fontSize: '16px',
      fontStyle: 'normal',
      color: '#111927',
      lineHeight: '25.12px',
    },
  };
};
